import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';

/**
 * Starts a countdown for `wait` ms.
 * Any invocations that happen during the countdown will reset the countdown
 * back to `wait` ms. When the countdown is done, the function `func` will be called.
 * @return The value that `func` returned when it was last called, or
 *   undefined if it hasn't been called yet.
 */
function debounceTrailing(func, wait) {
  let lastFuncResult = undefined;
  let timeoutId: Maybe<NodeJS.Timeout> = undefined;

  const debounced = (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      timeoutId = undefined;
      lastFuncResult = func(...args);
    }, wait);

    return lastFuncResult;
  };

  return debounced;
}

/**
 * Batch id fetching operations into single cached ones.
 * @param debounceMs The debounce time in ms.
 * @param fetchFn Function that fetches the elements by id.
 * @param mapper Function that maps the element to the id.
 * @returns Function that returns the cached elements for the given ids.
 */
export function newOperationBatch<T>(
  debounceMs: number,
  fetchFn: (id: string[]) => Promise<Maybe<T[]>>,
  mapper?: (elem: T) => string,
) {
  mapper ??= (elem: T) => {
    if (elem instanceof MongoEntity) {
      return elem._id;
    }
    return A.getGloballyUniqueID();
  };

  const cache: Record<string, Maybe<T>> = {};
  const toResolve: Record<string, ((res: Maybe<T>) => void)[]> = {};
  const nextFetch: string[] = [];
  const debouncedFetch = debounceTrailing(() => {
    if (nextFetch.length === 0) {
      return;
    }

    const elemsIds = [...nextFetch];
    nextFetch.length = 0;

    fetchFn(elemsIds).then(elems => {
      const idsFound: string[] = [];

      // * For all found elems we need to call toResolve with the mapped id and cache the elem
      elems?.forEach(elem => {
        if (elem) {
          const id = mapper(elem);
          idsFound.push(id);
          cache[id] = elem;
          toResolve[id]?.forEach(fn => fn(elem));
          delete toResolve[id];
        }
      });

      // * For all not found ids we need to call toResolve with null
      elemsIds.forEach(id => {
        if (!idsFound.includes(id)) {
          cache[id] = null;
          toResolve[id]?.forEach(fn => fn(null));
          delete toResolve[id];
        }
      });
    });
  }, debounceMs);

  const get = async (id: string): Promise<Maybe<T>> => {
    if (id === '') {
      return null;
    }

    if (cache[id] !== undefined) {
      return cache[id];
    }

    nextFetch.push(id);
    debouncedFetch();
    return new Promise(res => {
      toResolve[id] ??= [];
      toResolve[id]?.push(res);
    });
  };

  const getStored = (id?: Maybe<string>): Maybe<T> => {
    if (!id) {
      return null;
    }
    return cache[id] || null;
  };

  const toString = async (id: string, fmt: (elem: T) => Promise<string>, ifEmpty: string = ''): Promise<string> => {
    const elem = await get(id);
    if (!elem) {
      return ifEmpty;
    }
    return await fmt(elem);
  };

  return {
    get,
    toString,
    getStored,
  };
}
